import { useState, useEffect } from "react";

import { recordRecommendationClick } from "@/app/helpers/user-actions";
import { logger } from "@/lib/secure-logger";
import { Recommendation } from "@/types/dataSchema";

// Hook for managing recommendation handlers and state
export const useRecommendationHandlers = () => {
  const [contactModal, setContactModal] = useState<{
    isOpen: boolean;
    recommendation: Recommendation | null;
    action: any | null;
  }>({
    isOpen: false,
    recommendation: null,
    action: null,
  });

  const [generalContactModal, setGeneralContactModal] = useState(false);

  const [notifyModal, setNotifyModal] = useState<{
    isOpen: boolean;
    recommendation: Recommendation | null;
    action: any | null;
  }>({
    isOpen: false,
    recommendation: null,
    action: null,
  });

  const [notifiedRecommendations, setNotifiedRecommendations] = useState<
    Set<string>
  >(new Set());

  const [contactedRecommendations, setContactedRecommendations] = useState<
    Set<string>
  >(new Set());

  const [revealedCards, setRevealedCards] = useState<Set<number>>(new Set());
  const [shinyCards, setShinyCards] = useState<Set<number>>(new Set());
  const [replayTrigger, setReplayTrigger] = useState(0);
  const [showAllRecommendations, setShowAllRecommendations] = useState(false);
  const [additionalCardsRevealed, setAdditionalCardsRevealed] = useState<
    Set<number>
  >(new Set());
  const [animationStarted, setAnimationStarted] = useState(false);

  // Contact handlers
  const handleContactClick = (recommendation: Recommendation, action: any) => {
    setContactModal({
      isOpen: true,
      recommendation,
      action,
    });
  };

  const handleContactModalClose = () => {
    setContactModal({
      isOpen: false,
      recommendation: null,
      action: null,
    });
  };

  const handleContactSubmit = async (
    email: string,
    customRequest?: string,
    answers?: Record<string, any>,
    journeyData?: any,
  ) => {
    if (contactModal.recommendation && contactModal.action) {
      // Record the contact action
      await recordRecommendationClick({
        recommendationId: contactModal.recommendation.id,
        recommendationName: contactModal.recommendation.name,
        actionId: `${contactModal.recommendation.id}_contact_${contactModal.action.id}`,
        actionName: contactModal.action.name,
        actionUrl: contactModal.action.url || "",
        isCta: contactModal.action.cta || false,
      });

      // Debug logging - contact processed (no sensitive data)
      logger.debug("Contact request submitted", {
        hasEmail: !!email,
        hasCustomRequest: !!customRequest,
        recommendationName: contactModal.recommendation.name,
        actionName: contactModal.action.name,
        answersCount: answers ? Object.keys(answers).length : 0,
      });

      // Send backend notification (internal only, user doesn't see this)
      try {
        await fetch("/api/handle-contact-form", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            type: "contact",
            email,
            customRequest,
            recommendationId: contactModal.recommendation.id,
            recommendationName: contactModal.recommendation.name,
            actionId: `${contactModal.recommendation.id}_contact_${contactModal.action.id}`,
            actionName: contactModal.action.name,
            actionUrl: contactModal.action.url || "",
            userId: localStorage.getItem("crowdsec_user_id"),
            sessionId: localStorage.getItem("crowdsec_session_id"),
            answers,
            journeyData,
            timestamp: new Date().toISOString(),
          }),
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          "Failed to send backend notification for contact:",
          error,
        );
      }

      // Add the recommendation to the contacted recommendations set
      setContactedRecommendations((prev) =>
        new Set(prev).add(contactModal.recommendation!.id),
      );
    }
  };

  const handleGeneralContactSubmit = async (
    email: string,
    customRequest?: string,
  ) => {
    // Record the general contact action
    await recordRecommendationClick({
      recommendationId: "general_contact",
      recommendationName: "General Contact",
      actionId: "general_contact_request",
      actionName: "General Contact Request",
      actionUrl: "",
      isCta: true,
    });

    // Debug logging - general contact submitted
    logger.debug("General contact request submitted", {
      hasEmail: !!email,
      hasCustomRequest: !!customRequest,
    });
  };

  // Notify handlers
  const handleNotifyClick = (recommendation: Recommendation, action: any) => {
    setNotifyModal({
      isOpen: true,
      recommendation,
      action,
    });
  };

  const handleNotifyModalClose = () => {
    setNotifyModal({
      isOpen: false,
      recommendation: null,
      action: null,
    });
  };

  const handleNotifySubmit = async (
    email: string,
    answers?: Record<string, any>,
    journeyData?: any,
  ) => {
    if (notifyModal.recommendation && notifyModal.action) {
      // Record the notify action
      await recordRecommendationClick({
        recommendationId: notifyModal.recommendation.id,
        recommendationName: notifyModal.recommendation.name,
        actionId: `${notifyModal.recommendation.id}_notify_${notifyModal.action.id}`,
        actionName: notifyModal.action.name,
        actionUrl: notifyModal.action.url || "",
        isCta: notifyModal.action.cta || false,
      });

      // Debug logging - notify request submitted
      logger.debug("Notify request submitted", {
        hasEmail: !!email,
        recommendationName: notifyModal.recommendation.name,
        actionName: notifyModal.action.name,
        answersCount: answers ? Object.keys(answers).length : 0,
      });

      // Send backend notification (internal only, user doesn't see this)
      try {
        await fetch("/api/handle-contact-form", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            type: "notify",
            email,
            recommendationId: notifyModal.recommendation.id,
            recommendationName: notifyModal.recommendation.name,
            actionId: `${notifyModal.recommendation.id}_notify_${notifyModal.action.id}`,
            actionName: notifyModal.action.name,
            actionUrl: notifyModal.action.url || "",
            userId: localStorage.getItem("crowdsec_user_id"),
            sessionId: localStorage.getItem("crowdsec_session_id"),
            answers,
            journeyData,
            timestamp: new Date().toISOString(),
          }),
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to send backend notification for notify:", error);
      }

      // Add the recommendation to the notified recommendations set
      setNotifiedRecommendations((prev) =>
        new Set(prev).add(notifyModal.recommendation!.id),
      );
    }
  };

  // Animation handlers
  const handleTitleDoubleClick = () => {
    setReplayTrigger((prev) => prev + 1);
  };

  return {
    // Modal states
    contactModal,
    generalContactModal,
    setGeneralContactModal,
    notifyModal,

    // Recommendation states
    notifiedRecommendations,
    contactedRecommendations,

    // Animation states
    revealedCards,
    setRevealedCards,
    shinyCards,
    setShinyCards,
    replayTrigger,
    showAllRecommendations,
    setShowAllRecommendations,
    additionalCardsRevealed,
    setAdditionalCardsRevealed,
    animationStarted,
    setAnimationStarted,

    // Handlers
    handleContactClick,
    handleContactModalClose,
    handleContactSubmit,
    handleGeneralContactSubmit,
    handleNotifyClick,
    handleNotifyModalClose,
    handleNotifySubmit,
    handleTitleDoubleClick,
  };
};

// Hook for managing card animations
export const useCardAnimations = (
  recommendedRecommendations: Recommendation[],
  additionalRecommendations: Recommendation[],
  replayTrigger: number,
  showAllRecommendations: boolean,
  setRevealedCards: React.Dispatch<React.SetStateAction<Set<number>>>,
  setShinyCards: React.Dispatch<React.SetStateAction<Set<number>>>,
  setAnimationStarted: React.Dispatch<React.SetStateAction<boolean>>,
  setAdditionalCardsRevealed: React.Dispatch<React.SetStateAction<Set<number>>>,
) => {
  // Two-phase animation: reveal then shine (for initial 3 recommendations)
  useEffect(() => {
    // Only reset and start animation when we have recommendations to show
    if (recommendedRecommendations.length === 0) return;

    // Reset animations and mark as not started
    setAnimationStarted(false);
    setRevealedCards(new Set());
    setShinyCards(new Set());

    const timers: NodeJS.Timeout[] = [];

    // Small delay to ensure DOM is ready and CSS classes are applied
    const initialDelay = setTimeout(() => {
      setAnimationStarted(true);

      recommendedRecommendations.forEach((_, index) => {
        // Phase 1: Reveal card (blur to clear + slide up)
        const revealTimer = setTimeout(
          () => {
            setRevealedCards((prev) => new Set(prev).add(index));

            // Phase 2: Shine effect after reveal is complete
            const shineTimer = setTimeout(() => {
              setShinyCards((prev) => new Set(prev).add(index));

              // Remove the shiny effect after 2 seconds
              setTimeout(() => {
                setShinyCards((prev) => {
                  const newSet = new Set(prev);

                  newSet.delete(index);

                  return newSet;
                });
              }, 2000);
            }, 1200); // Wait for reveal animation to complete (1.2s)

            timers.push(shineTimer);
          },
          300 + index * 900,
        ); // Staggered reveal: 300ms, 1200ms, 2100ms

        timers.push(revealTimer);
      });
    }, 150); // Slightly longer delay to prevent any flash

    timers.push(initialDelay);

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [recommendedRecommendations.length, replayTrigger]);

  // Animation for additional recommendations when "show more" is clicked
  useEffect(() => {
    if (showAllRecommendations && additionalRecommendations.length > 0) {
      setAdditionalCardsRevealed(new Set());

      const timers: NodeJS.Timeout[] = [];

      additionalRecommendations.forEach((_, index) => {
        const actualIndex = recommendedRecommendations.length + index;
        const revealTimer = setTimeout(
          () => {
            setAdditionalCardsRevealed((prev) =>
              new Set(prev).add(actualIndex),
            );
          },
          200 + index * 300, // Faster reveal for additional cards
        );

        timers.push(revealTimer);
      });

      return () => {
        timers.forEach((timer) => clearTimeout(timer));
      };
    }
  }, [
    showAllRecommendations,
    additionalRecommendations.length,
    recommendedRecommendations.length,
  ]);
};
